import { UserType } from "../enums/users";
import { User } from "../interfaces/common/authentication";
import { UserRole } from "../interfaces/common/roles";

enum PERMISSION_CODES {
    ACCESS_CASHFLOW = "access_cashflow",
    UNDO_DELIVERY = "can_undo_delivery",
    VIEW_HOME_DASHBOARD = "can_view_home_dashboard",
    MANAGE_RECURRENT_EXPENSES = "can_manage_recurrent_expenses"
}


export const user_is_admin = (user: User | null): boolean => {
    if (user == null) return false;
    if (user.type === UserType.ORGANISATION) return true;
    return user.is_admin ? true : false
}

export const user_is_driver = (user: User | null): boolean => {
    return user != null && user.type === UserType.DRIVER
}

export const user_is_merchant = (user: User | null): boolean => {
    return user != null && user.type === UserType.MERCHANT
}

export const user_is_org_or_employee = (user: User | null) : boolean => {
    return user != null && (user.type == UserType.ORGANISATION || user.type == UserType.EMPLOYEE)
}

export const user_can_access_home = (user: User | null) : boolean => {
    if (user?.type == UserType.ORGANISATION) return true

    if (user?.is_admin) return true;

    
    return user != null && user.roles != undefined  && user.roles.some(role => role.permissions.some(perm => perm.code == PERMISSION_CODES.VIEW_HOME_DASHBOARD))
}

export enum GlobalPermissions {
    ACCESS_CASH_FLOW = "access_cashflow",
    UNDO_DELIVERY = "can_undo_delivery",
    VIEW_HOME_DASHBOARD = "can_view_home_dashboard",
    MANAGE_LABELS = "can_manage_labels",
    RECEIVE_FAILED_ORDERS = "can_receive_orders_at_warehouse",
    ACCESS_SHOP_BALANCE = "can_access_shop_balance"
}

export enum DataResources {
    ORDER = "order",
    DRIVER = "driver",
    CUSTOMER = "customer",
    MERCHANT = "merchant",
    EMPLOYEE = "employee",
    PICKUP_TASK = "pickuptask",
    DELIVERY_BATCH = "deliverybatch",
    EXPENSES = "expense",
    MERCHANT_INVOICE = "merchantinvoice",
    DRIVER_INVOICE = "driverinvoice",
    SHELF = "warehouseshelf",
    DELIVERY_FEE_RULES = "deliveryfeerule",
    RECURRING_PAYMENTS = "recurringpayment",
    EXPENSE_CATEGORIES = "expensecategory",
    // TODO: add labels models
}

export enum Page {
    HOME = "home",
    ORDERS = "orders",
    PICKUP_TASKS = "pickup-tasks",
    DELIVERY_BATCHES = "delivery-batches",
    CUSTOMERS = "customers",
    MERCHANTS = "merchants",
    DRIVERS = "drivers",
    EMPLOYEES = "employees",
    BRANCHES = "branches",
    SHELVES = "shelves",
    LABELS = "labels",
    ACCOUNTING = "accounting",
    EXPENSES = "expenses",
    MERCHANT_INVOICES = "merchant_invoices",
    DRIVER_INVOICES = "driver_invoices",
    RECURRING_PAYMENTS = "recurringpayment",
    SETTINGS = "settings"
}

export enum ResourceActionType {
    VIEW = "view",
    UPDATE = "update",
    CREATE = "create",
    DELETE = "delete"
}

export const user_has_data_model_access = (user: User | null, model: DataResources, action?: ResourceActionType) => {
    if (!user) return false
    if (user.type == UserType.ORGANISATION || user.is_admin) return true
    if (!user.roles) return false;
    return user.roles.some(role => role.permissions.some(perm => action == undefined ? perm.model == model : ( perm.model == model && perm.action == action) ))
}

export const user_has_global_permission = (user: User | null, globalPermCode: GlobalPermissions): boolean =>  {
    if (user?.type == UserType.ORGANISATION || user?.is_admin) return true   
    return (user?.roles ?? []).some(role => role.permissions.some(perm => perm.code == globalPermCode))
}
export const user_has_page_access = (user: User | null, page: Page): boolean => {
    if (!user) return false
    if (user.type == UserType.ORGANISATION || user.is_admin) return true
    if (!user.roles) return false

    switch(page) {
        case Page.HOME:
            return user_has_global_permission(user, GlobalPermissions.VIEW_HOME_DASHBOARD)
        case Page.ORDERS:
            return user_has_data_model_access(user, DataResources.ORDER)
        case Page.PICKUP_TASKS:
            return user_has_data_model_access(user, DataResources.PICKUP_TASK)
        case Page.DELIVERY_BATCHES:
            return user_has_data_model_access(user, DataResources.DELIVERY_BATCH)
        case Page.CUSTOMERS:
            return user_has_data_model_access(user, DataResources.CUSTOMER)
        case Page.MERCHANTS:
                return user_has_data_model_access(user, DataResources.MERCHANT)
        case Page.DRIVERS:
            return user_has_data_model_access(user, DataResources.DRIVER)
        case Page.EMPLOYEES:
            return user_has_data_model_access(user, DataResources.EMPLOYEE)
        case Page.SHELVES:
            return user_has_data_model_access(user, DataResources.SHELF)
        case Page.LABELS:
            return user_has_global_permission(user, GlobalPermissions.MANAGE_LABELS)
        case Page.ACCOUNTING:
            return user_has_data_model_access(user, DataResources.MERCHANT_INVOICE) || 
                    user_has_data_model_access(user, DataResources.MERCHANT_INVOICE) || 
                    user_has_data_model_access(user, DataResources.RECURRING_PAYMENTS ) ||
                    user_has_data_model_access(user, DataResources.EXPENSES) || 
                    user_has_global_permission(user, GlobalPermissions.ACCESS_CASH_FLOW)
        case Page.EXPENSES:
            return user_has_data_model_access(user, DataResources.EXPENSES)
        case Page.RECURRING_PAYMENTS:
            return user_has_data_model_access(user, DataResources.RECURRING_PAYMENTS)
        case Page.MERCHANT_INVOICES:
            return user_has_data_model_access(user, DataResources.MERCHANT_INVOICE)
        case Page.DRIVER_INVOICES:
            return user_has_data_model_access(user, DataResources.DRIVER_INVOICE)
        case Page.SETTINGS:
            return false
        case Page.BRANCHES:
            return false
    }
}

